import {
  FETCH_STATE_NONE,
  FETCH_STATE_PENDING,
  FETCH_STATE_SUCCESS,
  FETCH_STATE_ERROR,
} from '~/config/constants';

const defaultState = () => ({
  error: null,
  invoices: [],
  invoicesFetchState: FETCH_STATE_NONE,
  setStateInvoiceState: FETCH_STATE_NONE,
  fetchState: FETCH_STATE_NONE,
  requestParams: {
    searchQuery: null,
    searchOrder: {
      column: 'issued_on',
      direction: 'descending',
    },
    from: null,
    to: null,
    invoiceKind: null,
    period: null,
    onlyUnPaid: false,
    page: 1,
    total_pages: 1,
    invoicesForContact: null,
  },
});

export default {
  namespaced: true,
  state: defaultState(),
  mutations: {
    updateInvoicesData(state, action) {
      const invoice = action.payload.data;
      const index = state.invoices.findIndex(item => item.id === invoice.id);

      Object.assign(state.invoices[index], invoice);
    },
    setFetchStarted(state) {
      state.fetchState = FETCH_STATE_PENDING;
    },
    setFetchSuccess(state) {
      state.fetchState = FETCH_STATE_SUCCESS;
    },
    setFetchError(state) {
      state.fetchState = FETCH_STATE_ERROR;
    },
    setPaidStart(state) {
      state.setStateInvoiceState = FETCH_STATE_PENDING;
    },
    setPaidSuccess(state) {
      state.setStateInvoiceState = FETCH_STATE_SUCCESS;
    },
    setPaidError(state) {
      state.setStateInvoiceState = FETCH_STATE_ERROR;
    },
    getInvoicesSuccess(state, action) {
      const { page, total_pages: totalPages } = action.payload.headers;

      Object.assign(state.requestParams, {
        page: parseInt(page, 0),
        total_pages: parseInt(totalPages, 0),
      });

      Object.assign(state, action.payload.data);
      state.invoicesFetchState = FETCH_STATE_SUCCESS;
    },
    getInvoicesError(state) {
      Object.assign(state, defaultState());
      state.invoicesFetchState = FETCH_STATE_ERROR;
    },
    resetState(state) {
      Object.assign(state, defaultState());
    },
    setSearchParams(state, value) {
      Object.assign(state.requestParams, value);
    },
    invoicesForContact(state, value) {
      Object.assign(state.requestParams, { invoicesForContact: value });
    },
  },
  actions: {
    /* eslint-disable no-empty-pattern */
    invoicesForContact({ commit }, id) {
      commit('invoicesForContact', id);
    },
    invoicePreview({ dispatch }, { blob }) {
      if (window && window.open) {
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob);
        } else {
          const fileURL = window.URL.createObjectURL(blob);
          const newWindow = window.open('/preview');

          if (newWindow) {
            newWindow.onload = () => {
              newWindow.location = fileURL;
            };
          } else {
            dispatch('file/downloadFile', {
              payload: {
                headers: { 'content-disposition': "attachment; filename='preview.pdf'; filename*=UTF-8''preview.pdf" },
                data: blob,
              },
            }, { root: true });
          }
        }
      }
    },
    invoiceDownload({ commit, rootGetters }, {
      id, format = 'pdf', onSuccess = [], persistent = true, exportType = null, messageKey = 'Invoice', onFinish = () => {},
    }) {
      commit(
        'api/fetch',
        {
          url: `/invoices/${id}.${format}`,
          params: {
            export_type: exportType,
          },
          method: 'GET',
          responseType: 'blob',
          headers: rootGetters['auth/getHeaders'],
          invoiceId: id,
          onSuccess: [
            {
              type: 'flashMessages/addMessage',
              kind: 'success',
              key: `success${messageKey}Download`,
              withTimeout: true,
              persistent,
            },
            ...onSuccess,
          ],
          onError: [
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: `error${messageKey}Download`,
            },
          ],
          onFinish,
        },
        { root: true },
      );
    },
    invoiceDownloadBatch({ commit, rootGetters }, { ids, format }) {
      commit(
        'api/fetch',
        {
          url: '/invoices/batch',
          method: 'POST',
          responseType: 'blob',
          headers: rootGetters['auth/getHeaders'],
          onStart: [
            'invoices/setFetchStarted',
            {
              type: 'flashMessages/addMessage',
              kind: 'success',
              key: 'successInvoiceDownloadBatchStarted',
            },
          ],
          onSuccess: [
            'invoices/setFetchSuccess',
            {
              type: 'flashMessages/addMessage',
              kind: 'success',
              key: 'successInvoiceDownloadBatch',
            },
            'file/downloadFile',
          ],
          onError: [
            'invoices/setFetchError',
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: 'errorInvoiceDownloadBatch',
            },
          ],
          body: {
            invoices: {
              ids,
            },
            batch_action: `download_${format}`,
          },
        },
        { root: true },
      );
    },
    invoicesReport({ commit, rootGetters }) {
      commit(
        'api/fetch',
        {
          url: '/invoices/reports/last_year',
          method: 'POST',
          headers: rootGetters['auth/getHeaders'],
          onSuccess: [
            'api/setFetchSuccess',
            {
              type: 'flashMessages/addMessage',
              kind: 'success',
              key: 'successGetReportBatch',
              params: { email: rootGetters['auth/getUserEmail'] },
            },
          ],
          onError: [
            'api/setFetchError',
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: 'errorGetReportBatch',
            },
          ],
          body: {
            batch_action: 'request_pdf_report',
          },
        },
        { root: true },
      );
    },
    invoiceDeleteBatch({ commit, rootGetters }, ids) {
      commit(
        'api/fetch',
        {
          url: '/invoices/batch',
          method: 'POST',
          headers: rootGetters['auth/getHeaders'],
          onSuccess: [
            {
              type: 'flashMessages/addMessage',
              kind: 'success',
              key: 'successInvoiceDeleteBatch',
            },
            'api/refetchInvoices',
          ],
          onError: [
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: 'errorInvoiceDeleteBatch',
            },
          ],
          body: {
            invoices: {
              ids,
            },
            batch_action: 'destroy',
          },
        },
        { root: true },
      );
    },
    invoicePayBatch({ commit, rootGetters }, ids) {
      commit(
        'api/fetch',
        {
          url: '/invoices/batch',
          method: 'POST',
          headers: rootGetters['auth/getHeaders'],
          onSuccess: [
            {
              type: 'flashMessages/addMessage',
              kind: 'success',
              key: 'successInvoicePayBatch',
            },
            {
              type: 'api/refetchInvoicesByIds',
              ids,
            },
          ],
          onError: [
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: 'errorInvoicePayBatch',
            },
          ],
          body: {
            invoices: {
              ids,
            },
            batch_action: 'pay',
          },
        },
        { root: true },
      );
    },
    invoiceUnPayBatch({ commit, rootGetters }, ids) {
      commit(
        'api/fetch',
        {
          url: '/invoices/batch',
          method: 'POST',
          headers: rootGetters['auth/getHeaders'],
          onSuccess: [
            {
              type: 'flashMessages/addMessage',
              kind: 'success',
              key: 'successInvoiceUnPayBatch',
            },
            {
              type: 'api/refetchInvoicesByIds',
              ids,
            },
          ],
          onError: [
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: 'errorInvoiceUnPayBatch',
            },
          ],
          body: {
            invoices: {
              ids,
            },
            batch_action: 'unpay',
          },
        },
        { root: true },
      );
    },
    resetState({ commit }) {
      commit('resetState');
    },
    getNewInvoice({ commit, rootGetters }) {
      if (rootGetters['auth/isAuthorized']) {
        commit(
          'api/fetch',
          {
            url: '/invoices/new',
            method: 'GET',
            headers: {
              ...rootGetters['auth/getHeaders'],
            },
            onSuccess: ['invoiceForm/setInvoiceData'],
            onError: ['invoices/getInvoicesError'],
          },
          { root: true },
        );
      }
    },
  },
  getters: {
    isBatchDownloading(state) {
      return state.fetchState === FETCH_STATE_PENDING;
    },
    isSearching(state) {
      return state.requestParams.searchQuery !== null;
    },
    requestParams(state) {
      const { requestParams } = state;

      return {
        from: requestParams.from,
        to: requestParams.to,
        invoice_kind: requestParams.invoiceKind,
        period: requestParams.period,
        fulltext: requestParams.searchQuery,
        client_id: requestParams.invoicesForContact,
        page: requestParams.page,
        only_unpaid: requestParams.onlyUnPaid,
        sort: `${requestParams.searchOrder.column} ${requestParams.searchOrder.direction}`,
      };
    },
  },
};
